import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { mercuryAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isAccountant, isAdmin as roleIsAdmin } from "../lib/roles";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CreditCard, Receipt, Sparkle, MagnifyingGlass, Link as LinkIcon, PlusCircle, GasPump, Trash, Lightning, ArrowUUpLeft } from "@phosphor-icons/react";
import { formatDate } from "../lib/formatDate";
import { formatCurrency } from "../lib/formatCurrency";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import { QuickFilters } from "../components/QuickFilters";

function statusBadge(rawStatus) {
  const s = (rawStatus || "").toLowerCase();
  if (s === "pending") {
    return { label: "Pending", className: "bg-amber-50 text-amber-700 border-amber-200" };
  }
  if (s === "failed" || s === "cancelled" || s === "canceled") {
    return { label: s.charAt(0).toUpperCase() + s.slice(1), className: "bg-red-50 text-red-700 border-red-200" };
  }
  // sent / cleared / posted / completed all collapse to "Posted"
  return { label: "Posted", className: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

export default function TransactionsPage() {
  const location = useLocation();
  const initialState = location.state || {};
  const [transactions, setTransactions] = useState([]);
  const [unresolvedPreauths, setUnresolvedPreauths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState(() => {
    const f = {};
    ["submitted_by", "vendor", "card_last_four"].forEach((k) => { if (initialState[k]) f[k] = initialState[k]; });
    return f;
  });
  const [linkedFilter, setLinkedFilter] = useState(initialState.linked || "");
  // Manual pre-auth link dialog state
  const [linkDialog, setLinkDialog] = useState(null); // { preauth, candidates: [...] }
  const [linkBusy, setLinkBusy] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = isAccountant(user);
  const isAdmin = roleIsAdmin(user);

  const allocate = (txn) => {
    // Pass the current filter/list context so NewExpensePage knows where to
    // return after Save/Submit — keeps the cardholder's "work the unlinked
    // list one at a time" workflow intact without resetting filters.
    navigate("/expenses/new", {
      state: {
        transaction: txn,
        returnTo: {
          path: "/transactions",
          state: { ...filters, linked: linkedFilter },
        },
      },
    });
  };

  const fetchTxns = async () => {
    try {
      const params = { limit: 200 };
      if (filters.submitted_by) params.cardholder_user_id = filters.submitted_by;
      if (filters.vendor) params.vendor = filters.vendor;
      if (filters.card_last_four) params.card_last_four = filters.card_last_four;
      if (linkedFilter) params.linked = linkedFilter;
      const [txnRes, preauthRes] = await Promise.all([
        mercuryAPI.transactions(params),
        mercuryAPI.unresolvedPreauths(7).catch(() => ({ data: { transactions: [] } })),
      ]);
      setTransactions(txnRes.data.transactions);
      setUnresolvedPreauths(preauthRes.data.transactions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTxns(); }, [filters, linkedFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh: poll every 60s so the page reflects new card swipes without
  // requiring a manual reload. Daniel keeps this page open on a display all day.
  useEffect(() => {
    const id = setInterval(() => { fetchTxns(); }, 60000);
    return () => clearInterval(id);
  }, [filters, linkedFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSimulateSwipe = async () => {
    try {
      const { data } = await mercuryAPI.simulateSwipe();
      toast.success(`Training swipe created: ${data.merchant} — $${data.amount}`, {
        description: `Card ****${data.card_last_four} · Click Allocate to walk through expense capture.`,
      });
      await fetchTxns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to simulate swipe");
    }
  };

  const deleteSimulated = async (mercuryId, merchant) => {
    if (!confirm(`Delete training transaction "${merchant}"? This will also remove any linked expense.`)) return;
    try {
      const { data } = await mercuryAPI.deleteTransaction(mercuryId);
      toast.success(data.cascaded_expense_id ? "Training transaction + linked expense deleted" : "Training transaction deleted");
      await fetchTxns();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // Admin-only: tombstone a real card swipe so it disappears from the queue
  // and stays gone. Backend soft-deletes (sets `deleted: true`) which the
  // hourly Mercury backfill and the webhook ingest path both honour, so the
  // swipe won't come back.
  // Admin-only: one-shot scan that removes duplicate mercury_transactions
  // rows for the same mercury_id (keeps the one with the most useful state —
  // tombstoned > linked > oldest). Used to heal production duplicates that
  // slipped in before the backfill-respects-tombstones fix landed.
  const dedupeTxns = async () => {
    if (!confirm("Scan card transactions for duplicate rows and clean them up?\n\nThis hard-deletes extra copies that share the same Mercury ID. Safe to run anytime.")) return;
    try {
      const { data } = await mercuryAPI.dedupe();
      if (data.removed_rows > 0) {
        toast.success(`Cleaned ${data.removed_rows} duplicate row(s) across ${data.deduped_groups} swipe(s)`, {
          description: data.examples?.length
            ? `Examples: ${data.examples.map(e => `${e.merchant} $${Number(e.amount).toFixed(2)}`).join(", ")}`
            : undefined,
          duration: 8000,
        });
      } else {
        toast.success("No duplicates found — your card transactions are already clean");
      }
      await fetchTxns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dedupe failed");
    }
  };

  const deleteRealTxn = async (mercuryId, merchant, amount) => {
    const ok = confirm(
      `Permanently remove "${merchant}" — $${Number(amount).toFixed(2)} — from the card transactions queue?\n\nThis hides it from every list and prevents it from being re-ingested. Use this only for fraudulent, disputed, or otherwise irrelevant swipes.`
    );
    if (!ok) return;
    try {
      await mercuryAPI.deleteTransaction(mercuryId);
      toast.success(`Removed "${merchant}" from the queue`, { description: "It won't reappear on the next Mercury backfill." });
      await fetchTxns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const reSuggestVendors = async () => {
    setSuggesting(true);
    try {
      const { data } = await mercuryAPI.suggestVendors(true);
      toast.success(data.message);
      await fetchTxns();
    } catch (e) { toast.error(e.response?.data?.detail || "Suggestion run failed"); }
    finally { setSuggesting(false); }
  };

  const reconcileGas = async () => {
    setReconciling(true);
    try {
      const { data } = await mercuryAPI.reconcileGasPreauths();
      toast.success(data.message);
      await fetchTxns();
    } catch (e) { toast.error(e.response?.data?.detail || "Reconcile failed"); }
    finally { setReconciling(false); }
  };

  const deleteOrphan = async (mercuryId, merchant) => {
    if (!confirm(`Delete orphan pre-auth at "${merchant}"? This can't be undone.`)) return;
    try {
      await mercuryAPI.deleteTransaction(mercuryId);
      toast.success("Orphan pre-auth deleted");
      await fetchTxns();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const openLinkDialog = (preauth) => {
    // Candidates: real (non-preauth) transactions on the same card, within ±10 days, not already paired/linked.
    const preauthDate = new Date(preauth.date);
    const windowMs = 10 * 24 * 60 * 60 * 1000;
    const candidates = transactions.filter((t) => {
      if (t.mercury_id === preauth.mercury_id) return false;
      if (t.card_last_four !== preauth.card_last_four) return false;
      // Skip transactions already paired with a pre-auth
      if (t.supersedes_id) return false;
      // Skip $1.00 pre-auths
      if (t.amount >= 1 && t.amount <= 1.01) return false;
      const d = new Date(t.date);
      if (Math.abs(d - preauthDate) > windowMs) return false;
      return true;
    });
    setLinkDialog({ preauth, candidates });
  };

  const handleLinkPreauth = async (realTxn) => {
    if (!linkDialog) return;
    setLinkBusy(true);
    try {
      await mercuryAPI.linkPreauth(realTxn.mercury_id, linkDialog.preauth.mercury_id);
      toast.success(`Linked pre-auth to ${realTxn.merchant}`);
      setLinkDialog(null);
      await fetchTxns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link failed");
    } finally {
      setLinkBusy(false);
    }
  };

  const filtered = transactions.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (t.merchant || "").toLowerCase().includes(q) ||
      (t.suggested_vendor_name || "").toLowerCase().includes(q) ||
      (t.cardholder_name || "").toLowerCase().includes(q) ||
      (t.bank_description || "").toLowerCase().includes(q) ||
      (t.card_last_four || "").includes(q)
    );
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="transactions-page">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "DM Sans, sans-serif" }}>
            Card Transactions
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            {transactions.length} transactions &middot;{" "}
            <span className="text-zinc-600 font-medium">{transactions.filter((t) => t.expense_id).length}</span> linked to expenses
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!readOnly && (
            <>
              <Button onClick={handleSimulateSwipe} data-testid="simulate-swipe-btn"
                variant="outline" className="rounded-sm gap-1.5 text-xs border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900">
                <Lightning size={13} weight="fill" className="text-amber-500" />
                Simulate Swipe (Training)
              </Button>
              <Button onClick={reconcileGas} disabled={reconciling} data-testid="reconcile-gas-btn"
                variant="outline" className="rounded-sm gap-1.5 text-xs border-zinc-200">
                <GasPump size={13} weight="duotone" className={`text-[#002FA7] ${reconciling ? "animate-pulse" : ""}`} />
                {reconciling ? "Reconciling..." : "Reconcile gas pre-auths"}
              </Button>
              <Button onClick={reSuggestVendors} disabled={suggesting} data-testid="re-suggest-vendors-btn"
                variant="outline" className="rounded-sm gap-1.5 text-xs border-zinc-200">
                <Sparkle size={13} weight="duotone" className={`text-amber-500 ${suggesting ? "animate-spin" : ""}`} />
                {suggesting ? "Suggesting..." : "Re-run AI vendor suggestions"}
              </Button>
              {isAdmin && (
                <Button onClick={dedupeTxns} data-testid="dedupe-txns-btn"
                  variant="outline" className="rounded-sm gap-1.5 text-xs border-zinc-200"
                  title="Scan and clean up duplicate card transaction rows (admin-only heal tool).">
                  <Trash size={13} weight="duotone" className="text-zinc-500" />
                  Clean up duplicates
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              data-testid="txn-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by merchant, vendor, cardholder, last 4..."
              className="rounded-sm h-8 text-xs pl-8"
            />
          </div>
          {linkedFilter === "false" ? (
            <button
              onClick={() => setLinkedFilter("")}
              data-testid="clear-linked-filter"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-sm bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-50 transition-colors"
              title="Showing only unlinked swipes. Click to clear."
            >
              <LinkIcon size={11} weight="bold" /> Unlinked only <span className="text-amber-400">×</span>
            </button>
          ) : (
            // Visible toggle so the user can switch to the unlinked-only view
            // without having to deep-link from the Dashboard. Click flips
            // `linkedFilter` between "" (all) and "false" (only swipes with
            // no `expense_id`).
            <button
              onClick={() => setLinkedFilter("false")}
              data-testid="show-unlinked-only-btn"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-sm bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
              title="Show only swipes that need to be allocated"
            >
              <LinkIcon size={11} weight="bold" /> Unlinked only
            </button>
          )}
        </div>
        <QuickFilters value={filters} onChange={setFilters} show={["submitter", "vendor", "card"]} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center animate-pulse text-zinc-400">Loading...</div>
        ) : filtered.length > 0 ? (
          <div className="divide-y divide-zinc-100">
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
              <div className="col-span-2">Txn / Post Date</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Suggested Vendor</div>
              <div className="col-span-2">User (Cardholder)</div>
              <div className="col-span-1">Card</div>
              <div className="col-span-1">Description</div>
              <div className="col-span-1 text-right">Amount</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {filtered.map((t) => {
              const sb = statusBadge(t.status);
              const sim = !!t.is_simulated;
              // A negative amount or the explicit `is_refund` flag means this
              // is a credit-card credit (refund). We render the row with a
              // distinct REFUND badge + green amount so they can't be confused
              // with regular swipes at a glance.
              const isRefund = !!t.is_refund || (typeof t.amount === "number" && t.amount < 0);
              return (
                <div key={t.mercury_id} onClick={() => setSelected(t)}
                  className={`px-4 py-3 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center hover:bg-zinc-50 transition-colors cursor-pointer ${sim ? "bg-amber-50/60 border-l-4 border-amber-400" : isRefund ? "bg-emerald-50/40 border-l-4 border-emerald-400" : ""}`}
                  data-testid={`txn-row-${t.mercury_id}`}>
                  {/* Txn / Post date */}
                  <div className="col-span-2">
                    <p className="text-sm text-zinc-800 font-medium flex items-center gap-1.5 flex-wrap" data-testid={`txn-date-${t.mercury_id}`}>
                      {sim && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-500 text-white text-[9px] uppercase tracking-[0.15em] font-bold shadow-sm" data-testid={`txn-sim-badge-${t.mercury_id}`}>
                          <Lightning size={9} weight="fill" /> TEST
                        </span>
                      )}
                      {isRefund && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white text-[9px] uppercase tracking-[0.15em] font-bold shadow-sm" data-testid={`txn-refund-badge-${t.mercury_id}`}>
                          <ArrowUUpLeft size={9} weight="bold" /> REFUND
                        </span>
                      )}
                      {formatDate(t.transaction_date || t.date)}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5" data-testid={`post-date-${t.mercury_id}`}>
                      Posted: {t.post_date ? <span className="text-zinc-700 font-medium">{formatDate(t.post_date)}</span> : <span className="text-zinc-300">—</span>}
                    </p>
                  </div>
                  {/* Status */}
                  <div className="col-span-1 mt-1 sm:mt-0">
                    <Badge data-testid={`txn-status-${t.mercury_id}`} className={`text-[9px] uppercase tracking-wider rounded-sm ${sb.className}`}>
                      {sb.label}
                    </Badge>
                  </div>
                  {/* Suggested Vendor */}
                  <div className="col-span-2 mt-1 sm:mt-0">
                    {t.suggested_vendor_name ? (
                      <div className="flex items-center gap-1.5">
                        <Sparkle size={11} weight="duotone" className="text-amber-500 flex-shrink-0" />
                        <span className="text-sm text-zinc-800 font-medium truncate" title={t.suggested_vendor_name}>{t.suggested_vendor_name}</span>
                        {t.suggested_vendor_confidence != null && (
                          <span className="text-[9px] font-mono text-zinc-400 flex-shrink-0">
                            {Math.round(t.suggested_vendor_confidence * 100)}%
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-300 italic">No match</span>
                    )}
                  </div>
                  {/* Cardholder */}
                  <div className="col-span-2 mt-1 sm:mt-0">
                    <p className="text-sm text-zinc-700 truncate">{t.cardholder_name || <span className="text-zinc-300 italic">Unlinked</span>}</p>
                  </div>
                  {/* Card */}
                  <div className="col-span-1 mt-1 sm:mt-0">
                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-zinc-100 border border-zinc-200">
                      <CreditCard size={10} className="text-zinc-500" />
                      <span className="text-[11px] font-mono font-bold text-zinc-700">{t.card_last_four || "—"}</span>
                    </div>
                  </div>
                  {/* Description */}
                  <div className="col-span-1 mt-1 sm:mt-0">
                    <p className="text-xs text-zinc-500 line-clamp-2" title={t.bank_description || t.note || ""}>
                      {t.bank_description || t.note || <span className="text-zinc-300">—</span>}
                    </p>
                  </div>
                  {/* Amount */}
                  <div className="col-span-1 text-right mt-1 sm:mt-0">
                    <span className={`text-sm font-bold font-mono whitespace-nowrap ${sim ? "text-amber-700" : isRefund ? "text-emerald-700" : "text-zinc-900"}`}>{formatCurrency(t.amount)}</span>
                  </div>
                  {/* Action */}
                  <div className="col-span-2 text-right mt-1 sm:mt-0 flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {t.expense_id ? (
                      <Badge className="text-[9px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200 gap-0.5">
                        <LinkIcon size={9} weight="bold" /> Linked
                      </Badge>
                    ) : !readOnly ? (
                      <Button size="sm" onClick={() => allocate(t)} data-testid={`allocate-txn-${t.mercury_id}`}
                        className="h-7 px-2 bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm text-xs gap-1">
                        <PlusCircle size={12} weight="bold" /> Allocate
                      </Button>
                    ) : (
                      <span className="text-[10px] text-zinc-300 italic">Unlinked</span>
                    )}
                    {!readOnly && sim && (
                      <Button size="sm" variant="ghost" onClick={() => deleteSimulated(t.id, t.merchant)} data-testid={`delete-sim-txn-${t.mercury_id}`}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-sm" title="Delete training transaction">
                        <Trash size={12} weight="bold" />
                      </Button>
                    )}
                    {!readOnly && isAdmin && !sim && !t.expense_id && (
                      <Button size="sm" variant="ghost" onClick={() => deleteRealTxn(t.id, t.merchant, t.amount)}
                        data-testid={`delete-real-txn-${t.mercury_id}`}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                        title="Remove this swipe from the queue (admin only — soft delete, won't return on Mercury sync)">
                        <Trash size={12} weight="bold" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Receipt size={40} weight="duotone" className="text-zinc-200 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">{search ? "No transactions match your search." : "No transactions yet. Swipes will appear here as soon as Mercury sends the webhook."}</p>
          </div>
        )}
      </div>

      {/* Unresolved gas pre-auths (admin) */}
      {unresolvedPreauths.length > 0 && (
        <div className="mt-8" data-testid="unresolved-preauths-section">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <GasPump size={14} weight="duotone" className="text-amber-500" />
              <h2 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                Unresolved Gas Pre-Auths &mdash; {unresolvedPreauths.length}
              </h2>
            </div>
            <p className="text-[10px] text-zinc-400 max-w-md">
              Fuel pre-auths older than 7 days that never matched a real charge. Verify with Mercury, then delete.
            </p>
          </div>
          <div className="bg-amber-50/30 border border-amber-200 rounded-sm overflow-hidden">
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2 bg-amber-50 text-[10px] uppercase tracking-[0.15em] font-bold text-amber-700">
              <div className="col-span-2">Date</div>
              <div className="col-span-3">Merchant</div>
              <div className="col-span-2">Cardholder</div>
              <div className="col-span-1">Card</div>
              <div className="col-span-2">Description</div>
              <div className="col-span-1 text-right">Amount</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            <div className="divide-y divide-amber-100">
              {unresolvedPreauths.map((t) => (
                <div key={t.mercury_id} className="px-4 py-2.5 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center hover:bg-amber-50/60" data-testid={`unresolved-preauth-${t.mercury_id}`}>
                  <div className="col-span-2 text-sm text-zinc-700">{formatDate(t.date)}</div>
                  <div className="col-span-3 text-sm text-zinc-800 truncate">{t.merchant}</div>
                  <div className="col-span-2 text-sm text-zinc-600 truncate">{t.cardholder_name || <span className="text-zinc-300 italic">Unlinked</span>}</div>
                  <div className="col-span-1">
                    <span className="text-[11px] font-mono font-bold text-zinc-700">****{t.card_last_four || "—"}</span>
                  </div>
                  <div className="col-span-2 text-xs text-zinc-500 truncate" title={t.bank_description}>{t.bank_description || "—"}</div>
                  <div className="col-span-1 text-right text-sm font-bold font-mono text-zinc-900">{formatCurrency(t.amount)}</div>
                  <div className="col-span-1 text-right flex justify-end gap-0.5">
                    {!readOnly && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openLinkDialog(t)}
                          data-testid={`link-preauth-${t.mercury_id}`}
                          className="h-7 w-7 p-0 text-zinc-500 hover:text-[#002FA7] hover:bg-blue-50"
                          title="Link to a real charge">
                          <LinkIcon size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteOrphan(t.id, t.merchant)}
                          data-testid={`delete-orphan-${t.mercury_id}`}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          title="Delete">
                          <Trash size={12} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Manual Link Pre-Auth Dialog */}
      <Dialog open={!!linkDialog} onOpenChange={() => setLinkDialog(null)}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="link-preauth-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "DM Sans, sans-serif" }}>
              <GasPump size={18} weight="duotone" className="text-amber-500" />
              Link Pre-Auth to Real Charge
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {linkDialog && (
                <>Pick the real charge that this <span className="font-mono font-bold">{formatCurrency(linkDialog.preauth.amount)}</span> pre-auth at <span className="font-semibold">{linkDialog.preauth.merchant}</span> belongs to.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {linkDialog && (
            <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
              {linkDialog.candidates.length === 0 ? (
                <div className="p-6 text-center bg-zinc-50 rounded-sm border border-zinc-100">
                  <p className="text-sm text-zinc-500">No candidate real charges on card <span className="font-mono font-bold">****{linkDialog.preauth.card_last_four}</span> within ±10 days.</p>
                  <p className="text-xs text-zinc-400 mt-1">Try loading more transactions or check that the real charge has cleared.</p>
                </div>
              ) : (
                linkDialog.candidates.map((c) => (
                  <button
                    key={c.mercury_id}
                    onClick={() => handleLinkPreauth(c)}
                    disabled={linkBusy}
                    data-testid={`link-candidate-${c.mercury_id}`}
                    className="w-full text-left px-3 py-2.5 rounded-sm border border-zinc-200 hover:border-[#002FA7] hover:bg-blue-50/40 transition-colors flex items-center gap-3 disabled:opacity-50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{c.merchant}</p>
                      <p className="text-[11px] text-zinc-400 truncate">
                        {formatDate(c.date)} · ****{c.card_last_four} · {c.cardholder_name || "Unlinked"}
                      </p>
                    </div>
                    <span className="text-sm font-bold font-mono text-zinc-900 flex-shrink-0">{formatCurrency(c.amount)}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="txn-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight" style={{ fontFamily: "DM Sans, sans-serif" }}>
              Transaction Detail
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-2 mt-2 text-sm">
              <Row label="Transaction Date" value={formatDate(selected.transaction_date || selected.date)} />
              <Row label="Post Date" value={selected.post_date ? formatDate(selected.post_date) : "— (pending)"} />
              <Row label="Amount" value={<span className="font-mono font-bold">{formatCurrency(selected.amount)}</span>} />
              <Row label="Raw merchant" value={selected.merchant || "—"} />
              <Row label="Suggested QBO vendor" value={
                selected.suggested_vendor_name ? (
                  <span className="flex items-center gap-1.5">
                    <Sparkle size={12} weight="duotone" className="text-amber-500" />
                    {selected.suggested_vendor_name}
                    {selected.suggested_vendor_confidence != null && (
                      <span className="text-[10px] font-mono text-zinc-400">{Math.round(selected.suggested_vendor_confidence * 100)}% match</span>
                    )}
                  </span>
                ) : <span className="text-zinc-400">No confident match</span>
              } />
              <Row label="Cardholder" value={selected.cardholder_name || <span className="text-zinc-400">Unlinked</span>} />
              <Row label="Card" value={`**** ${selected.card_last_four || "?"}`} />
              <Row label="Status" value={
                (() => {
                  const sb = statusBadge(selected.status);
                  return <Badge className={`text-[9px] uppercase tracking-wider rounded-sm ${sb.className}`}>{sb.label}</Badge>;
                })()
              } />
              <Row label="Description" value={selected.bank_description || selected.note || "—"} />
              {selected.expense_id && <Row label="Linked expense" value={<Badge className="text-[9px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">Yes</Badge>} />}
              {!selected.expense_id && !readOnly && (
                <div className="pt-2 border-t border-zinc-100">
                  <Button onClick={() => { allocate(selected); setSelected(null); }} data-testid="dialog-allocate-btn"
                    className="w-full bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm gap-1.5">
                    <PlusCircle size={14} weight="bold" /> Allocate to an expense
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{label}</span>
      <span className="col-span-2 text-zinc-800 break-words">{value}</span>
    </div>
  );
}
